import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { FormInput } from "go-form";
import useFlash from "go-alert/AlertMessage";
import ImageColorForm from "go-app/components/ImageForm/ImageColorForm";
import handleError from "go-app/services/errors";
import { FormDirty } from "go-form/components/FormDirty";
import FormNumberInput from "go-form/components/FormNumberInput";
import { ProfileFormProps } from "../../../../../../../../../services/Api/User/userTypes";
import { api } from "../../../../../../../../../services/Security/api";
import { UserActionTypes, UserState } from "../../../../../../../../../services/users/types";

interface Props {
	userProfile: UserState;
}

const mapToForm = (user: UserState) => {
	return {
		name: user?.name,
		avatar: user?.avatar,
		avatar_link: user?.avatar_link,
		user_phone_number: user?.user_phone_number,
	};
};

const ProfileForm = (props: Props) => {
	const form = useForm<ProfileFormProps>({
		criteriaMode: "all",
		defaultValues: mapToForm(props.userProfile),
	});
	const { addFlash, addSuccessFlash } = useFlash();
	const {
		register,
		handleSubmit,
		formState: { errors },
		setError,
		setValue,
		formState,
		reset,
		getValues,
		control,
	} = form;
	const [loading, setLoading] = useState(false);
	const { t } = useTranslation();
	const dispatch = useDispatch();

	const handleSave = (imageBase: string | undefined) => {
		if (props.userProfile) {
			props.userProfile.avatar_link = undefined;
		}
		if (imageBase) {
			setValue("avatar.type", "IMAGE");
			setValue("avatar.image_data", imageBase);
		} else {
			setValue("avatar.image_data", undefined);
		}
	};

	const getParsedFormData = (data: ProfileFormProps) => {
		if (!getValues("avatar.image_data")) {
			data.avatar = undefined;
		}
		data.avatar_link = props.userProfile?.avatar_link;

		return data;
	};

	const onSubmit = handleSubmit(async (data) => {
		setLoading(true);
		const newData = getParsedFormData(data);
		try {
			const res = await api.user().saveUser(Number(props?.userProfile?.id), newData, {
				include: "user_organizations,organizations,organizations.applications",
			});
			addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
			if (props.userProfile) {
				props.userProfile.avatar_link = res.data?.data?.avatar_link;
			}
			setValue("avatar.image_data", undefined);
			dispatch({ type: UserActionTypes.SET, payload: res.data?.data });
			reset({ ...res.data?.data, user_phone_number: res.data?.data?.phone_number });
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
	});

	return (
		<FormDirty formState={formState} key="profile-form" noValidate onSubmit={onSubmit} loading={loading}>
			<fieldset className={"form-group no-gutters"}>
				<div className="row">
					<div className={"col-md-10"}>
						<div className="row">
							<div className="col-md-6">
								<FormInput
									label={t("modules.profile.field.full_name.title")}
									register={register}
									name="name"
									errors={errors}
								/>
							</div>
							<div className="col-md-6">
								<FormNumberInput
									label={t("common.word.phone_number", { ns: "lib" })}
									name="user_phone_number"
									errors={errors}
									control={control}
									suffix=""
								/>
							</div>
						</div>
					</div>
					<div className="col-md-2">
						<ImageColorForm
							data={props.userProfile}
							resourceLink={props.userProfile.avatar_link}
							handleSave={handleSave}
							form={form}
							prefix="avatar"
							mode="IMAGE"
						/>
					</div>
				</div>
			</fieldset>
		</FormDirty>
	);
};

export default ProfileForm;
